import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  IconPlus, IconLock, IconLayoutBottombar, IconTrash,
  IconPencil, IconCheck, IconX, IconDotsVertical, IconEye,
} from '@tabler/icons-react'
import { containersApi, type Container } from '../../lib/api.ts'
import { ContainerReassignModal } from '../../components/ui/ContainerReassignModal.tsx'
import { useToast } from '../../components/ui/Toast.tsx'
import { InlineError } from '../../components/ui/ErrorBoundary.tsx'
import { FullScreenModal } from '../../components/ui/FullScreenModal.tsx'

// ── Types ─────────────────────────────────────────────────────────────────────

type DeleteState =
  | { type: 'idle' }
  | { type: 'confirm'; container: Container }
  | { type: 'last-locker'; container: Container }
  | { type: 'reassign'; container: Container }

// ── Add Container Form ────────────────────────────────────────────────────────

function AddContainerForm({
  open, onClose, onSaved, typeName, parentId, parentName
}: {
  open: boolean; onClose: () => void; onSaved: () => void
  typeName: 'locker' | 'bin'; parentId?: string; parentName?: string
}) {
  const { success, error } = useToast()
  const [name, setName] = useState('')
  const [barcode, setBarcode] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await containersApi.create({ name: name.trim(), barcode: barcode.trim() || null, typeName, parentId })
      success(`${typeName === 'locker' ? 'Locker' : 'Gear Bag'} created`, name)
      setName('')
      setBarcode('')
      onSaved()
      onClose()
    } catch (e: any) {
      error(`Failed to create ${typeName}`, e.message)
    } finally {
      setSaving(false)
    }
  }

  const label = typeName === 'locker' ? 'New locker' : `New Gear Bag${parentName ? ` in ${parentName}` : ''}`

  return (
    <FullScreenModal open={open} onClose={onClose} title={label}
      subtitle={typeName === 'locker' ? 'Add a locker to your collection' : 'Add a gear bag to organise your gear'}
      narrow
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving || !name.trim()}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
      <div className="max-w-lg mx-auto px-6 py-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-zinc-200 mb-1.5">Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder={typeName === 'locker' ? 'e.g. Home Locker, Bathhouse Bag...' : 'e.g. Hockey gear, Leather collection...'}
            className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors"
            autoFocus />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-200 mb-1.5">Label / Barcode</label>
          <input value={barcode} onChange={e => setBarcode(e.target.value)}
            placeholder="e.g. physical label, barcode, QR code value..."
            className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors" />
        </div>
      </div>
    </FullScreenModal>
  )
}

// ── Confirm Delete Dialog ─────────────────────────────────────────────────────

function ConfirmDeleteDialog({
  open, name, isLastLocker, onConfirm, onCancel
}: {
  open: boolean; name: string; isLastLocker?: boolean; onConfirm: () => void; onCancel: () => void
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
        {isLastLocker ? (
          <>
            <div className="w-10 h-10 rounded-full bg-amber-950 border border-amber-800 flex items-center justify-center mb-4">
              <IconLock size={18} className="text-amber-400" />
            </div>
            <h3 className="text-sm font-semibold text-zinc-100 mb-2">Cannot delete last locker</h3>
            <p className="text-sm text-zinc-400 mb-4">
              At least one locker must exist. Create another locker before deleting "{name}".
            </p>
            <button onClick={onCancel}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-surface-raised border border-border-default text-zinc-300 hover:text-zinc-100 transition-colors">
              OK
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-red-950 border border-red-800 flex items-center justify-center mb-4">
              <IconTrash size={18} className="text-red-400" />
            </div>
            <h3 className="text-sm font-semibold text-zinc-100 mb-2">Delete "{name}"?</h3>
            <p className="text-sm text-zinc-400 mb-5">This action cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={onCancel}
                className="flex-1 px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-400 hover:text-zinc-200 transition-colors">
                Cancel
              </button>
              <button onClick={onConfirm}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 transition-colors">
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Inline name editor ────────────────────────────────────────────────────────

function InlineEdit({ value, onSave, onCancel }: { value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  return (
    <div className="flex items-center gap-1 flex-1">
      <input value={v} onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSave(v); if (e.key === 'Escape') onCancel() }}
        className="flex-1 px-2 py-0.5 bg-surface-raised border border-brand-orange/50 rounded text-sm text-zinc-200 outline-none"
        autoFocus onClick={e => e.stopPropagation()} />
      <button onClick={e => { e.stopPropagation(); onSave(v) }}
        className="p-1 rounded text-emerald-400 hover:bg-surface-raised transition-colors"><IconCheck size={13} /></button>
      <button onClick={e => { e.stopPropagation(); onCancel() }}
        className="p-1 rounded text-zinc-500 hover:bg-surface-raised transition-colors"><IconX size={13} /></button>
    </div>
  )
}

// ── Bin row — inside a locker card ────────────────────────────────────────────

function BinRow({ bin, lockerId, onRename, onDelete }: {
  bin: Container
  lockerId: string
  onRename: (bin: Container, newName: string) => void
  onDelete: (bin: Container) => void
}) {
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)

  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-surface-base group transition-colors cursor-pointer"
      onClick={() => !editing && navigate(`/lockers/${lockerId}/bins?edit=${bin.id}`)}
    >
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-zinc-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="2" y="7" width="20" height="14" rx="2"/>
        <path d="M2 11h20" strokeLinecap="round"/>
        <path d="M7 7V5a2 2 0 014 0v2" strokeLinecap="round"/>
      </svg>

      {editing ? (
        <InlineEdit
          value={bin.name}
          onSave={name => { onRename(bin, name); setEditing(false) }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <span className="flex-1 text-xs text-zinc-300 truncate">{bin.name}</span>
          {bin.barcode && (
            <span className="text-[10px] text-zinc-600 font-mono">{bin.barcode}</span>
          )}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={e => { e.stopPropagation(); setEditing(true) }}
              className="p-1 rounded text-zinc-500 hover:text-zinc-300 hover:bg-surface-raised transition-colors">
              <IconPencil size={11} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDelete(bin) }}
              className="p-1 rounded text-zinc-500 hover:text-red-400 hover:bg-surface-raised transition-colors">
              <IconTrash size={11} />
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Locker Card — 2-part card: header + bins ──────────────────────────────────

function LockerCard({ locker, allContainers, onRefresh, onDeleteRequest, onDismissHover }: {
  locker: Container
  allContainers: Container[]
  onRefresh: () => void
  onDeleteRequest: (container: Container) => void
  onDismissHover: () => void
}) {
  const { error } = useToast()
  const [editing, setEditing] = useState(false)
  const [addBinOpen, setAddBinOpen] = useState(false)

  const bins = allContainers.filter(c => c.parentId === locker.id)

  const handleRename = async (container: Container, newName: string) => {
    if (!newName.trim() || newName === container.name) return
    try {
      await containersApi.update(container.id, { name: newName.trim() })
      onRefresh()
    } catch (e: any) {
      error('Rename failed', e.message)
    }
  }

  return (
    <div className="bg-surface-overlay border border-border-default rounded-xl overflow-hidden">

      {/* ── Locker header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
        <svg viewBox="0 0 24 24" className="w-4 h-4 text-zinc-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="6" width="18" height="15" rx="2"/>
          <path d="M3 10h18" strokeLinecap="round"/>
          <circle cx="12" cy="14.5" r="1.5"/>
          <path d="M12 16v2" strokeLinecap="round"/>
          <path d="M8 6V5a4 4 0 018 0v1" strokeLinecap="round"/>
        </svg>

        {editing ? (
          <InlineEdit
            value={locker.name}
            onSave={name => { handleRename(locker, name); setEditing(false) }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-zinc-200 truncate">
              {locker.name}
              {locker.isDefault && (
                <span className="ml-2 text-[10px] font-normal text-zinc-500 uppercase tracking-widest">Default</span>
              )}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setEditing(true)}
                className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-surface-raised transition-colors">
                <IconPencil size={13} />
              </button>
              <button
                onClick={() => onDeleteRequest(locker)}
                className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-surface-raised transition-colors">
                <IconTrash size={13} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Bins section ── */}
      <div className="px-2 py-2">
        {bins.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-2">
            <IconLayoutBottombar size={13} className="text-zinc-600 shrink-0" />
            <span className="text-xs text-zinc-600 italic">No gear bags — gear stored directly in locker</span>
          </div>
        ) : (
          <div className="space-y-0.5">
            {bins.map(bin => (
              <BinRow
                key={bin.id}
                bin={bin}
                lockerId={locker.id}
                onRename={handleRename}
                onDelete={onDeleteRequest}
              />
            ))}
          </div>
        )}

        {/* Add bin button */}
        <button
          onClick={() => { onDismissHover(); setAddBinOpen(true) }}
          className="flex items-center gap-2 w-full px-3 py-2 mt-1 rounded-lg text-xs text-zinc-500 hover:text-zinc-300 hover:bg-surface-raised transition-colors border border-dashed border-zinc-700 hover:border-zinc-500">
          <IconPlus size={12} />
          Add gear bag
        </button>
      </div>

      <AddContainerForm
        open={addBinOpen}
        onClose={() => setAddBinOpen(false)}
        onSaved={onRefresh}
        typeName="bin"
        parentId={locker.id}
        parentName={locker.name}
      />
    </div>
  )
}

// ── LockersPage ───────────────────────────────────────────────────────────────

export function LockersPage() {
  const { success, error } = useToast()
  const { id: selectedLockerId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const lockerRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [containers, setContainers] = useState<Container[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [addLockerOpen, setAddLockerOpen] = useState(() => searchParams.get('action') === 'new')
  const [deleteState, setDeleteState] = useState<DeleteState>({ type: 'idle' })

  // Hover card state — wired when hover card is implemented
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [hoverItem, setHoverItem] = useState<null>(null)
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null)

  const dismissHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoverItem(null)
    setHoverRect(null)
  }

  const openAddLocker = () => { dismissHover(); setAddLockerOpen(true) }

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [lockersRes, binsRes] = await Promise.all([
        containersApi.list({ type: 'locker' }),
        containersApi.list({ type: 'bin' }),
      ])
      setContainers([...(lockersRes.data ?? []), ...(binsRes.data ?? [])])
    } catch (e: any) {
      setLoadError(e.message)
      error('Failed to load lockers', e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (searchParams.get('action') === 'new') setSearchParams({}, { replace: true })
  }, [])

  useEffect(() => {
    if (!selectedLockerId || loading) return
    const el = lockerRefs.current.get(selectedLockerId)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [selectedLockerId, loading])

  const lockers = containers.filter(c => c.isTopLevel)

  const handleDeleteRequest = async (container: Container) => {
    dismissHover()
    if (container.isTopLevel && lockers.length <= 1) {
      setDeleteState({ type: 'last-locker', container })
      return
    }
    try {
      const res = await containersApi.contents(container.id)
      const hasContents =
        (res.data.gearItems?.length ?? 0) > 0 ||
        (res.data.bins?.length ?? 0) > 0
      if (hasContents) {
        setDeleteState({ type: 'reassign', container })
      } else {
        setDeleteState({ type: 'confirm', container })
      }
    } catch {
      setDeleteState({ type: 'confirm', container })
    }
  }

  const handleDeleteConfirm = async () => {
    if (deleteState.type !== 'confirm') return
    const { container } = deleteState
    setDeleteState({ type: 'idle' })
    try {
      await containersApi.delete(container.id)
      success('Deleted', container.name)
      load()
    } catch (e: any) {
      if (e.message?.includes('last locker') || e.message?.includes('At least one')) {
        setDeleteState({ type: 'last-locker', container })
      } else {
        error('Delete failed', e.message)
      }
    }
  }

  const handleReassignResolved = async () => {
    if (deleteState.type !== 'reassign') return
    const { container } = deleteState
    setDeleteState({ type: 'idle' })
    try {
      await containersApi.delete(container.id)
      success('Deleted', container.name)
      load()
    } catch (e: any) {
      error('Delete failed', e.message)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default">
        <h1 className="text-sm font-medium text-zinc-200 flex-1">
          My Lockers
          <span className="ml-2 text-zinc-500 font-normal text-xs">· {lockers.length} {lockers.length === 1 ? 'locker' : 'lockers'}</span>
        </h1>
        <button onClick={openAddLocker}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors">
          <IconPlus size={14} /> Add Locker
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loadError ? (
          <InlineError message={loadError} onRetry={load} />
        ) : loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-zinc-500">Loading...</div>
        ) : lockers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <div className="w-14 h-14 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mb-4">
              <IconLock size={22} className="text-zinc-600" />
            </div>
            <h3 className="text-sm font-medium text-zinc-400 mb-1">No lockers yet</h3>
            <p className="text-xs text-zinc-500 mb-4">Create a locker to start organising your gear.</p>
            <button onClick={openAddLocker}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-medium hover:bg-brand-orange-dark transition-colors">
              <IconPlus size={14} /> Create your first locker
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 max-w-2xl">
            {lockers.map(locker => (
              <div
                key={locker.id}
                ref={el => { if (el) lockerRefs.current.set(locker.id, el); else lockerRefs.current.delete(locker.id) }}
                className={selectedLockerId === locker.id ? 'ring-2 ring-brand-orange/40 rounded-xl' : ''}>
                <LockerCard
                  locker={locker}
                  allContainers={containers}
                  onRefresh={load}
                  onDeleteRequest={handleDeleteRequest}
                  onDismissHover={dismissHover}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <AddContainerForm open={addLockerOpen} onClose={() => setAddLockerOpen(false)}
        onSaved={load} typeName="locker" />

      <ConfirmDeleteDialog
        open={deleteState.type === 'confirm' || deleteState.type === 'last-locker'}
        name={deleteState.type !== 'idle' ? deleteState.container.name : ''}
        isLastLocker={deleteState.type === 'last-locker'}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteState({ type: 'idle' })}
      />

      {deleteState.type === 'reassign' && (
        <ContainerReassignModal
          container={deleteState.container}
          allContainers={containers}
          onResolved={handleReassignResolved}
          onCancel={() => setDeleteState({ type: 'idle' })}
        />
      )}
    </div>
  )
}
